import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { retentionPolicySchema } from '../schema/retentionPolicy.schema';
import type { RetentionPolicyFormValues } from '../schema/retentionPolicy.schema';
import type { RetentionPolicyResponse } from '../types/retentionAdmin';

/** 허용 테이블 목록 — application.yml eraf.retention.allowed-tables 와 일치 */
const ALLOWED_TABLES = [
  'biz_auditlog_audit_log',
  'cmn_scheduler_run_history',
  'cmn_security_audit_log',
  'cmn_batch_job_execution',
  'cmn_messaging_event_dlq',
  'cmn_core_idempotency_key',
  'cmn_security_token_blacklist',
  'cmn_observability_slow_query',
];

interface RetentionPolicyFormDialogProps {
  mode: 'create' | 'edit';
  initial?: RetentionPolicyResponse;
  isPending: boolean;
  serverError?: string | null;
  onSubmit: (values: RetentionPolicyFormValues) => void;
  onClose: () => void;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p
      role="alert"
      style={{
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-danger)',
        marginTop: 'var(--spacing-1)',
      }}
    >
      {message}
    </p>
  );
}

/**
 * RetentionPolicyFormDialog — 정책 생성/수정 폼
 * react-hook-form + zod · ESC 닫기 · focus trap
 */
export function RetentionPolicyFormDialog({
  mode,
  initial,
  isPending,
  serverError,
  onSubmit,
  onClose,
}: RetentionPolicyFormDialogProps) {
  const { t } = useTranslation();

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<RetentionPolicyFormValues>({
    resolver: zodResolver(retentionPolicySchema),
    defaultValues: {
      name: initial?.name ?? '',
      description: initial?.description ?? '',
      targetTable: initial?.targetTable ?? '',
      mode: initial?.mode ?? 'ABSOLUTE',
      timeColumn: initial?.timeColumn ?? 'updated_at',
      retentionDays: initial?.retentionDays ?? undefined,
      graceDays: initial?.graceDays ?? undefined,
      batchSize: initial?.batchSize ?? undefined,
      enabled: initial?.enabled ?? false,
    },
  });

  const selectedMode = watch('mode');

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    reset({
      name: initial?.name ?? '',
      description: initial?.description ?? '',
      targetTable: initial?.targetTable ?? '',
      mode: initial?.mode ?? 'ABSOLUTE',
      timeColumn: initial?.timeColumn ?? 'updated_at',
      retentionDays: initial?.retentionDays ?? undefined,
      graceDays: initial?.graceDays ?? undefined,
      batchSize: initial?.batchSize ?? undefined,
      enabled: initial?.enabled ?? false,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.id]);

  const inputStyle = (hasError: boolean) => ({
    width: '100%',
    padding: 'var(--spacing-2) var(--spacing-3)',
    border: `1px solid ${hasError ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    background: 'var(--color-surface-elevated)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    boxSizing: 'border-box' as const,
  });

  const labelStyle = {
    display: 'block',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    fontWeight: 600,
    marginBottom: 'var(--spacing-1)',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="policy-form-dialog-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
        overflowY: 'auto',
        padding: 'var(--spacing-4)',
      }}
    >
      <div
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          width: 560,
          maxWidth: '100%',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-5)',
          }}
        >
          <h2
            id="policy-form-dialog-title"
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
            }}
          >
            {mode === 'create'
              ? t('retentionAdmin.form.title.create')
              : t('retentionAdmin.form.title.edit')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 'var(--font-size-xl)',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            &#x2715;
          </button>
        </div>

        {/* 서버 오류 */}
        {serverError && (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-3)',
              background: 'var(--color-surface-muted)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            {serverError}
          </div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}
        >
          {/* 정책명 */}
          <div>
            <label htmlFor="policy-name" style={labelStyle}>
              {t('retentionAdmin.form.field.name')} <span aria-hidden="true">*</span>
            </label>
            <input
              id="policy-name"
              type="text"
              placeholder={t('retentionAdmin.form.field.namePlaceholder')}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'policy-name-error' : undefined}
              {...register('name')}
              style={inputStyle(!!errors.name)}
            />
            <FieldError message={errors.name?.message} />
          </div>

          {/* 설명 */}
          <div>
            <label htmlFor="policy-desc" style={labelStyle}>
              {t('retentionAdmin.form.field.description')}
            </label>
            <textarea
              id="policy-desc"
              placeholder={t('retentionAdmin.form.field.descriptionPlaceholder')}
              rows={2}
              {...register('description')}
              style={{
                ...inputStyle(false),
                resize: 'vertical',
                fontFamily: 'var(--font-family-sans)',
              }}
            />
          </div>

          {/* 대상 테이블 */}
          <div>
            <label htmlFor="policy-table" style={labelStyle}>
              {t('retentionAdmin.form.field.table')} <span aria-hidden="true">*</span>
            </label>
            {mode === 'create' ? (
              <select
                id="policy-table"
                aria-invalid={!!errors.targetTable}
                aria-describedby="policy-table-hint"
                {...register('targetTable')}
                style={inputStyle(!!errors.targetTable)}
              >
                <option value="">{t('retentionAdmin.list.filter.tableAll')}</option>
                {ALLOWED_TABLES.map((tbl) => (
                  <option key={tbl} value={tbl}>
                    {tbl}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="policy-table"
                type="text"
                disabled
                value={initial?.targetTable ?? ''}
                style={{ ...inputStyle(false), opacity: 0.6, cursor: 'not-allowed' }}
              />
            )}
            <p
              id="policy-table-hint"
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              {t('retentionAdmin.form.field.tableHint')}
            </p>
            <FieldError message={errors.targetTable?.message} />
          </div>

          {/* 모드 */}
          <div>
            <label htmlFor="policy-mode" style={labelStyle}>
              {t('retentionAdmin.form.field.mode')} <span aria-hidden="true">*</span>
            </label>
            <select
              id="policy-mode"
              aria-invalid={!!errors.mode}
              {...register('mode')}
              style={inputStyle(!!errors.mode)}
            >
              <option value="ABSOLUTE">{t('retentionAdmin.mode.ABSOLUTE')}</option>
              <option value="SOFT_THEN_HARD">{t('retentionAdmin.mode.SOFT_THEN_HARD')}</option>
            </select>
            <FieldError message={errors.mode?.message} />
          </div>

          {/* 기준 시간 컬럼 */}
          <div>
            <label htmlFor="policy-timecolumn" style={labelStyle}>
              {t('retentionAdmin.form.field.timeColumn')} <span aria-hidden="true">*</span>
            </label>
            <input
              id="policy-timecolumn"
              type="text"
              placeholder={t('retentionAdmin.form.field.timeColumnPlaceholder')}
              aria-invalid={!!errors.timeColumn}
              {...register('timeColumn')}
              style={inputStyle(!!errors.timeColumn)}
            />
            <FieldError message={errors.timeColumn?.message} />
          </div>

          {/* ABSOLUTE 모드 전용: 보존 기간 */}
          {selectedMode === 'ABSOLUTE' && (
            <div>
              <label htmlFor="policy-retentiondays" style={labelStyle}>
                {t('retentionAdmin.form.field.retentionDays')} <span aria-hidden="true">*</span>
              </label>
              <Controller
                name="retentionDays"
                control={control}
                render={({ field }) => (
                  <input
                    id="policy-retentiondays"
                    type="number"
                    min={0}
                    aria-invalid={!!errors.retentionDays}
                    placeholder="예: 90"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                    }
                    style={inputStyle(!!errors.retentionDays)}
                  />
                )}
              />
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  marginTop: 'var(--spacing-1)',
                }}
              >
                {t('retentionAdmin.form.field.retentionDaysHint')}
              </p>
              <FieldError message={errors.retentionDays?.message} />
            </div>
          )}

          {/* SOFT_THEN_HARD 모드 전용: 유예 기간 */}
          {selectedMode === 'SOFT_THEN_HARD' && (
            <div>
              <label htmlFor="policy-gracedays" style={labelStyle}>
                {t('retentionAdmin.form.field.graceDays')} <span aria-hidden="true">*</span>
              </label>
              <Controller
                name="graceDays"
                control={control}
                render={({ field }) => (
                  <input
                    id="policy-gracedays"
                    type="number"
                    min={0}
                    aria-invalid={!!errors.graceDays}
                    placeholder="예: 30"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                    }
                    style={inputStyle(!!errors.graceDays)}
                  />
                )}
              />
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  marginTop: 'var(--spacing-1)',
                }}
              >
                {t('retentionAdmin.form.field.graceDaysHint')}
              </p>
              <FieldError message={errors.graceDays?.message} />
            </div>
          )}

          {/* 배치 크기 */}
          <div>
            <label htmlFor="policy-batchsize" style={labelStyle}>
              {t('retentionAdmin.form.field.batchSize')}
            </label>
            <Controller
              name="batchSize"
              control={control}
              render={({ field }) => (
                <input
                  id="policy-batchsize"
                  type="number"
                  min={1}
                  max={100000}
                  aria-invalid={!!errors.batchSize}
                  placeholder="기본값 1000"
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                  }
                  style={inputStyle(!!errors.batchSize)}
                />
              )}
            />
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              {t('retentionAdmin.form.field.batchSizeHint')}
            </p>
            <FieldError message={errors.batchSize?.message} />
          </div>

          {/* 활성화 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <input
                  id="policy-enabled"
                  type="checkbox"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
              )}
            />
            <label
              htmlFor="policy-enabled"
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
              }}
            >
              {t('retentionAdmin.form.field.enabled')}
            </label>
          </div>

          {/* 버튼 */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-2)',
              justifyContent: 'flex-end',
              marginTop: 'var(--spacing-2)',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('retentionAdmin.form.button.cancel')}
            </button>
            <button
              type="submit"
              disabled={isPending}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: isPending ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                color: isPending ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: isPending ? 'not-allowed' : 'pointer',
                opacity: isPending ? 0.7 : 1,
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {isPending ? '...' : t('retentionAdmin.form.button.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
